import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiSearch,
  HiDownload,
  HiLocationMarker,
  HiDotsVertical,
  HiKey,
  HiClipboard,
  HiRefresh,
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiInformationCircle,
  HiTag,
} from 'react-icons/hi';
import { exportInvoiceToExcel } from '../utils/excelExport';
import Select from './Select';
import ClientSearchSelect from './ClientSearchSelect';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import { SkeletonTable } from './Skeleton';
import { PERMISSAO_MAP } from '../types';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import PhoneInput from './PhoneInput';
import InvoiceLabelSheet from './InvoiceLabelSheet';
import { exportInvoiceLabelPaperToPng } from '../utils/invoiceLabelExport';
import './Invoices.css';

const Invoices = () => {
  const navigate = useNavigate();
  const { userRole, loading: permissionsLoading } = usePermissions();
  const isInvoicesAdmin = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);

  // Função para obter data/hora atual no timezone de Brasília (America/Sao_Paulo)
  const getBrazilianDateTime = () => {
    // Obter data/hora atual no timezone de Brasília (America/Sao_Paulo)
    const now = new Date();
    // Converter para timezone de Brasília e formatar como ISO string (YYYY-MM-DDTHH:mm)
    const brazilianDateTime = now.toLocaleString('sv-SE', {
      timeZone: 'America/Sao_Paulo',
    });
    // Formato retornado: "2026-01-18 16:30:45" -> precisamos "2026-01-18T16:30"
    // Substituir o espaço por 'T' e pegar apenas os primeiros 16 caracteres (YYYY-MM-DDTHH:mm)
    return brazilianDateTime.replace(' ', 'T').slice(0, 16);
  };

  // Função para obter data e hora no formato ISO no timezone de Brasília
  const getBrazilianDateTimeISO = () => {
    const now = new Date();
    // Converter para timezone de Brasília e formatar como ISO string
    const brazilianDateTime = now.toLocaleString('sv-SE', {
      timeZone: 'America/Sao_Paulo',
    });
    // Formato retornado: "2026-01-18 16:30:45" -> converter para "2026-01-18T16:30:45"
    return brazilianDateTime.replace(' ', 'T');
  };

  const [invoices, setInvoices] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    numero: '',
    codigo_rastreamento: '',
    cliente: '',
    cliente_email: '',
    cliente_telefone: '',
    cliente_cep: '',
    cliente_endereco: '',
    cliente_numero: '',
    cliente_cidade: '',
    cliente_estado: '',
    cliente_pais: '',
    origem: '',
    destino: '',
    caixas_malas: [],
    status: 'pendente',
    etapas: [],
    fornecedor_id: '',
    custo_envio: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [currentBox, setCurrentBox] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [showTrackingHistoryModal, setShowTrackingHistoryModal] =
    useState(false);
  const [selectedInvoiceForTracking, setSelectedInvoiceForTracking] =
    useState(null);
  const [trackingHistory, setTrackingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [trackingForm, setTrackingForm] = useState({
    status: '',
    descricao: '',
    local_pacote: '',
    localizacao: '',
    latitude: '',
    longitude: '',
    evento_em: getBrazilianDateTime(),
  });
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]); // Todas as invoices para verificação
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [selectedInvoiceCodes, setSelectedInvoiceCodes] = useState(null);
  const [showInvoiceLabelModal, setShowInvoiceLabelModal] = useState(false);
  const [invoiceForLabel, setInvoiceForLabel] = useState(null);
  const [invoiceLabelMalasQty, setInvoiceLabelMalasQty] = useState(1);
  const [invoiceLabelDownloading, setInvoiceLabelDownloading] = useState(false);
  const invoiceLabelsPreviewRef = useRef(null);
  const { notifications, removeNotification, showSuccess, showWarning } =
    useNotification();

  useEffect(() => {
    loadInvoices();
    loadBoxes();
    loadFornecedores();
    loadInvoiceStatuses();
    loadAllInvoices(); // Carregar todas as invoices para verificação
    loadClients(); // Carregar clientes para o select
  }, []);

  // Carregar todas as invoices (sem paginação) para verificação de caixas já usadas
  const loadAllInvoices = async () => {
    try {
      const data = await apiService.getInvoicesList({
        page: 1,
        per_page: 10000, // Número grande para pegar todas
      });
      const invoicesList = normalizeApiData(data);

      // Mapear os campos da API para o formato esperado
      const invoicesWithDefaults = invoicesList.map(invoice => {
        // Extrair pacotes/caixas de diferentes formatos possíveis
        let pacotes = [];
        if (invoice.pacotes && Array.isArray(invoice.pacotes)) {
          pacotes = invoice.pacotes;
        } else if (
          invoice.caixas_malas &&
          Array.isArray(invoice.caixas_malas)
        ) {
          pacotes = invoice.caixas_malas;
        } else if (invoice.boxes && Array.isArray(invoice.boxes)) {
          pacotes = invoice.boxes;
        } else if (invoice.packages && Array.isArray(invoice.packages)) {
          pacotes = invoice.packages;
        }

        return {
          ...invoice,
          id: invoice.id,
          pacotes,
          caixas_malas: pacotes, // Manter compatibilidade
        };
      });

      setAllInvoices(invoicesWithDefaults);
    } catch (error) {
      console.error('Erro ao carregar todas as invoices:', error);
      setAllInvoices([]);
    }
  };

  const loadClients = async () => {
    try {
      const perPage = 500;
      let page = 1;
      const all = [];
      for (;;) {
        const response = await apiService.getClientsList({
          page,
          per_page: perPage,
        });
        const list = normalizeApiData(response);
        if (!Array.isArray(list) || list.length === 0) break;
        all.push(...list);
        const lastPage = response?.last_page ?? response?.lastPage;
        if (lastPage !== undefined && lastPage !== null) {
          if (page >= Number(lastPage)) break;
          page += 1;
          continue;
        }
        if (list.length < perPage) break;
        page += 1;
      }
      setClients(
        all.map(c => ({
          id: c.id,
          nome: (c.nome || c.name || '').trim(),
          email: c.email || '',
          telefone: c.telefone || c.phone || '',
        }))
      );
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    }
  };

  const loadInvoiceStatuses = async () => {
    try {
      const data = await apiService.getInvoiceStatus();
      // Extrair status do campo data se existir
      const statusesList = data?.data || normalizeApiData(data);
      setInvoiceStatuses(Array.isArray(statusesList) ? statusesList : []);
    } catch (error) {
      console.error('Erro ao carregar status de invoice:', error);
      setInvoiceStatuses([]);
    }
  };

  const loadFornecedores = async () => {
    try {
      const data = await apiService.getFornecedor();
      const fornecedoresList = normalizeApiData(data);
      setFornecedores(fornecedoresList);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      setFornecedores([]);
    }
  };

  useEffect(() => {
    // Recarregar invoices quando searchTerm ou paginação mudarem
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, itemsPerPage]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (openMenuId && !event.target.closest('.actions-menu-wrapper')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuId]);

  const loadBoxes = async () => {
    try {
      const perPage = 500;
      let page = 1;
      const allBoxes = [];

      for (;;) {
        const response = await apiService.getBoxes({
          page,
          per_page: perPage,
        });
        const boxesList = normalizeApiData(response);
        if (!Array.isArray(boxesList) || boxesList.length === 0) break;
        allBoxes.push(...boxesList);

        const lastPage = response?.last_page ?? response?.lastPage;
        if (lastPage !== undefined && lastPage !== null) {
          if (page >= Number(lastPage)) break;
          page += 1;
          continue;
        }
        if (boxesList.length < perPage) break;
        page += 1;
      }

      // Mapear os campos da API para o formato esperado pelo componente
      const boxesWithDefaults = allBoxes.map(box => ({
        id: box.id,
        codigo: box.codigo_pacote || box.codigo || '',
        cliente_nome: box.cliente?.nome || box.cliente_nome || '',
        valor_remessa: box.valor_declarado || box.valor_remessa || 'R$ 0,00',
        peso: box.peso_bruto || box.peso || '',
        status: box.status || '',
        origem: box.origem || '',
        destino: box.destino || '',
        tipo: box.tipo || '',
      }));
      setBoxes(boxesWithDefaults);
    } catch (error) {
      console.error('Erro ao carregar caixas/malas:', error);
      setBoxes([]);
    }
  };

  // Função para gerar hash robusto a partir de uma string
  const generateHash = str => {
    // Usar algoritmo de hash mais robusto
    let hash = 0;
    if (str.length === 0) return hash.toString();

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    // Adicionar timestamp para garantir unicidade
    const timestamp = Date.now();
    hash = hash + timestamp;

    // Converter para string base36 e pegar primeiros caracteres
    const hashStr = Math.abs(hash).toString(36).toUpperCase();

    // Combinar com timestamp para criar hash único
    const timestampStr = timestamp.toString(36).toUpperCase();

    // Retornar hash de 8 caracteres (combinando hash e timestamp)
    return (hashStr + timestampStr).substring(0, 8);
  };

  const generateInvoiceNumber = () => {
    // Obter data e hora atual no timezone de Brasília
    const now = new Date();
    const brazilianDateTime = now.toLocaleString('sv-SE', {
      timeZone: 'America/Sao_Paulo',
    });

    // Formato: YYYY-MM-DD HH:mm:ss -> remover espaços e caracteres especiais
    const dateTimeString = brazilianDateTime.replace(/[-:\s]/g, '');

    // Adicionar milissegundos para garantir unicidade
    const timestamp = now.getTime();
    const uniqueString = `${dateTimeString}${timestamp}`;

    // Criar hash a partir da data e hora
    const hash = generateHash(uniqueString);

    // Retornar no formato INV + hash
    return `INV${hash}`;
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await apiService.getInvoicesList({
        search: searchTerm,
        page: currentPage,
        per_page: itemsPerPage,
      });
      const invoicesList = normalizeApiData(data);

      // Mapear os campos da API para o formato esperado pelo componente
      const invoicesWithDefaults = invoicesList.map(invoice => ({
        ...invoice,
        id: invoice.id,
        numero:
          invoice.numero_documento || invoice.numero || invoice.title || '',
        codigo_rastreamento:
          invoice.numero_documento ||
          invoice.numero ||
          invoice.codigo_rastreamento ||
          invoice.tracking_code ||
          '',
        cliente:
          invoice.cliente_destinatario ||
          invoice.clientName ||
          invoice.cliente ||
          invoice.cliente_nome ||
          '',
        cliente_email:
          invoice.cliente_email ||
          invoice.email_cliente ||
          invoice.client_email ||
          '',
        cliente_telefone:
          invoice.cliente_telefone ||
          invoice.telefone_cliente ||
          invoice.client_phone ||
          '',
        cliente_cep:
          invoice.cliente_cep || invoice.cep || invoice.client_cep || '',
        cliente_endereco:
          invoice.cliente_endereco ||
          invoice.endereco ||
          invoice.client_address ||
          '',
        cliente_numero:
          invoice.cliente_numero ||
          invoice.numero ||
          invoice.client_number ||
          '',
        cliente_cidade:
          invoice.cliente_cidade || invoice.cidade || invoice.client_city || '',
        cliente_estado:
          invoice.cliente_estado ||
          invoice.estado ||
          invoice.client_state ||
          '',
        cliente_pais:
          invoice.cliente_pais || invoice.pais || invoice.client_country || '',
        origem: invoice.origem || invoice.origin || '',
        destino: invoice.destino || invoice.destination || '',
        data:
          invoice.data_emissao ||
          invoice.startDate ||
          invoice.data ||
          new Date().toISOString().split('T')[0],
        valor: (() => {
          const rawValue =
            invoice.valor_total || invoice.total_value || invoice.valor;
          if (!rawValue) return 'R$ 0,00';
          // Se já está formatado com R$, retorna como está
          if (typeof rawValue === 'string' && rawValue.includes('R$')) {
            return rawValue;
          }
          // Formata o valor usando displayCurrency
          return displayCurrency(rawValue);
        })(),
        peso_total: invoice.peso_total || invoice.peso || 0,
        status:
          invoice.status?.status_invoice ||
          invoice.status_invoice ||
          (invoice.status_id ? String(invoice.status_id) : 'pendente'),
        status_id: invoice.status_id || invoice.status?.id || null,
        status_obj: invoice.status || null,
        // Preservar pacotes original para uso no handleEdit
        pacotes: invoice.pacotes || [],
        caixas_malas:
          invoice.pacotes || invoice.caixas_malas || invoice.boxes || [],
        etapas: invoice.etapas || invoice.steps || [],
        fornecedor_id: invoice.fornecedor_id || invoice.supplier_id || '',
        custo_envio: invoice.custo_envio || invoice.shipping_cost || '',
        // Mapear codigos_acesso e garantir formato correto
        codigos_acesso: (() => {
          let codigos = invoice.codigos_acesso;
          // Se for string JSON, fazer parse
          if (typeof codigos === 'string' && codigos.trim() !== '') {
            try {
              codigos = JSON.parse(codigos);
            } catch (e) {
              console.error('Erro ao fazer parse de codigos_acesso:', e);
              codigos = null;
            }
          }
          // Garantir formato padrão com todas as chaves
          return codigos && typeof codigos === 'object'
            ? {
                administrador: codigos.administrador || '',
                gerente: codigos.gerente || '',
                transportador: codigos.transportador || '',
                entregador: codigos.entregador || '',
              }
            : {
                administrador: '',
                gerente: '',
                transportador: '',
                entregador: '',
              };
        })(),
      }));

      setInvoices(invoicesWithDefaults);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar invoices:', error);
      setInvoices([]);
      setLoading(false);
    }
  };

  const handleAddBox = () => {
    if (!currentBox) {
      showWarning('Selecione uma caixa/mala');
      return;
    }

    const selectedBox = boxes.find(b => b.id === Number(currentBox));
    if (!selectedBox) {
      showWarning('Caixa/mala não encontrada');
      return;
    }

    // Validar se origem e destino da invoice correspondem à caixa
    if (formData.origem && formData.destino) {
      const origemMatch =
        selectedBox.origem &&
        selectedBox.origem.toLowerCase().trim() ===
          formData.origem.toLowerCase().trim();
      const destinoMatch =
        selectedBox.destino &&
        selectedBox.destino.toLowerCase().trim() ===
          formData.destino.toLowerCase().trim();

      if (!origemMatch || !destinoMatch) {
        showWarning(
          `A caixa/mala selecionada tem origem "${selectedBox.origem || 'não informada'}" e destino "${selectedBox.destino || 'não informado'}", que não correspondem à origem "${formData.origem}" e destino "${formData.destino}" da fatura.`
        );
        return;
      }
    }

    if (!formData.caixas_malas.includes(Number(currentBox))) {
      setFormData({
        ...formData,
        caixas_malas: [...formData.caixas_malas, Number(currentBox)],
      });
      setCurrentBox('');
    }
  };

  const handleRemoveBox = boxId => {
    setFormData({
      ...formData,
      caixas_malas: formData.caixas_malas.filter(b => b !== boxId),
    });
  };

  // Função para formatar valor de moeda no input (formato brasileiro: 0,00)
  const handleCurrencyInput = value => {
    if (!value) return '';
    // Remove tudo exceto números
    const numbers = value.replace(/\D/g, '');
    if (numbers === '') return '';
    // Converte para número e divide por 100 para ter centavos
    const number = parseFloat(numbers) / 100;
    // Formata como moeda brasileira (sem símbolo R$)
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Função para exibir valor formatado
  const displayCurrency = value => {
    if (!value) return 'R$ 0,00';
    if (typeof value === 'string' && value.includes('R$')) {
      return value;
    }

    let number = 0;
    if (typeof value === 'string') {
      // Se é uma string numérica simples (ex: "5641.06" ou "5641,06")
      // Remove caracteres não numéricos exceto ponto e vírgula
      const cleanValue = value.replace(/[^\d,.-]/g, '');

      // Se tem vírgula, assume formato brasileiro (ex: "5641,06")
      if (cleanValue.includes(',')) {
        number =
          parseFloat(cleanValue.replace(/\./g, '').replace(',', '.')) || 0;
      }
      // Se tem apenas ponto, assume formato americano (ex: "5641.06")
      else if (cleanValue.includes('.')) {
        number = parseFloat(cleanValue) || 0;
      }
      // Se não tem nem ponto nem vírgula, é um número inteiro
      else {
        number = parseFloat(cleanValue) || 0;
      }
    } else {
      number = value || 0;
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(number);
  };

  // Função para converter valor formatado de moeda para número decimal
  const parseCurrencyValue = value => {
    if (!value) return 0;
    // Remove espaços, pontos (milhares) e converte vírgula para ponto
    const numberStr = value
      .toString()
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim();
    const parsed = parseFloat(numberStr);
    // Retorna com 2 casas decimais (formato 10,2)
    return isNaN(parsed) ? 0 : parseFloat(parsed.toFixed(2));
  };

  const calculateInvoiceTotal = () => {
    const selectedBoxes = boxes.filter(b =>
      formData.caixas_malas.includes(b.id)
    );
    return selectedBoxes
      .reduce((total, b) => {
        const valorStr = b.valor_remessa || 'R$ 0,00';
        // Remove R$, pontos e espaços, substitui vírgula por ponto
        const value =
          parseFloat(
            valorStr
              .replace('R$', '')
              .replace(/\./g, '')
              .replace(',', '.')
              .trim()
          ) || 0;
        return total + value;
      }, 0)
      .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.cliente || !String(formData.cliente).trim()) {
      showWarning('Selecione o cliente destinatário.');
      return;
    }

    if (formData.caixas_malas.length === 0) {
      showWarning('Adicione pelo menos uma caixa/mala');
      return;
    }

    try {
      setSaving(true);
      // Calcular cargoWeight
      const cargoWeight = formData.caixas_malas.reduce((total, boxId) => {
        const box = boxes.find(b => b.id === boxId);
        if (box) {
          const peso =
            parseFloat(
              box.peso ||
                box.peso_bruto ||
                box.valor_remessa?.replace(/[^\d,.-]/g, '').replace(',', '.')
            ) || 0;
          return total + peso;
        }
        return total;
      }, 0);

      // Converter status para ID numérico
      // formData.status já deve ser o ID (string ou número)
      let statusId = null;
      if (formData.status) {
        // Se for número, usar diretamente. Se for string, converter para número
        if (typeof formData.status === 'number') {
          statusId = formData.status;
        } else if (typeof formData.status === 'string') {
          const parsed = parseInt(formData.status);
          if (!isNaN(parsed)) {
            statusId = parsed;
          }
        }
      }

      // Preparar dados para API conforme especificação
      const invoiceData = {
        title: formData.numero || `Invoice ${formData.numero}`,
        description: `Invoice ${formData.numero} - ${formData.cliente}`,
        startDate: getBrazilianDateTimeISO(),
        endDate: getBrazilianDateTimeISO(),
        status_id: statusId,
        clientName: formData.cliente || '',
        cargoWeight,
        origin: formData.origem || '',
        destination: formData.destino || '',
        assignedUsers: [],
        fornecedor_id: formData.fornecedor_id
          ? parseInt(formData.fornecedor_id)
          : null,
        custo_envio: formData.custo_envio
          ? parseCurrencyValue(formData.custo_envio)
          : null,
        packages: formData.caixas_malas.map(id => parseInt(id)),
        cliente_destinatario: formData.cliente || '',
        email_cliente: formData.cliente_email || '',
        telefone_cliente: formData.cliente_telefone || '',
        cep: formData.cliente_cep || '',
        endereco: formData.cliente_endereco || '',
        numero: formData.cliente_numero || '',
        cidade: formData.cliente_cidade || '',
        estado: formData.cliente_estado || '',
        pais: formData.cliente_pais || '',
      };

      if (editingId) {
        // Para atualização, adicionar progress se necessário
        const updateData = {
          ...invoiceData,
          progress: 0, // Você pode ajustar isso se necessário
        };
        await apiService.updateInvoice(editingId, updateData);
        showSuccess('Fatura atualizada com sucesso!');
      } else {
        await apiService.createInvoice(invoiceData);
        showSuccess('Fatura criada com sucesso!');
      }

      // Recarregar invoices da API
      await loadInvoices();
      await loadAllInvoices(); // Recarregar todas as invoices para verificação
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar fatura:', error);
      showWarning(error.message || 'Erro ao salvar fatura');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = invoice => {
    if (!isInvoicesAdmin) return;
    // Formatar custo_envio: converter de string decimal para formato brasileiro
    let custoEnvioFormatted = '';
    if (invoice.custo_envio) {
      const custoEnvioValue =
        typeof invoice.custo_envio === 'string'
          ? parseFloat(invoice.custo_envio)
          : invoice.custo_envio;
      if (!isNaN(custoEnvioValue) && custoEnvioValue > 0) {
        // Converter para formato de moeda brasileira (12.400,00)
        custoEnvioFormatted = custoEnvioValue.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
    }

    // Mapear pacotes para caixas_malas (array de IDs)
    const caixasMalasIds = Array.isArray(invoice.pacotes)
      ? invoice.pacotes.map(pacote => pacote.id).filter(id => id != null)
      : Array.isArray(invoice.caixas_malas)
        ? invoice.caixas_malas
        : [];

    // Garantir que status seja o ID numérico
    let statusId = '';
    if (invoice.status_id) {
      statusId = String(invoice.status_id);
    } else if (invoice.status && invoiceStatuses.length > 0) {
      // Se status é um objeto com id, usar o id
      if (typeof invoice.status === 'object' && invoice.status.id) {
        statusId = String(invoice.status.id);
      } else if (typeof invoice.status === 'string') {
        // Se status é string (nome), buscar o ID correspondente
        const statusObj = invoiceStatuses.find(
          s =>
            s.status_invoice === invoice.status ||
            s.status_invoice?.toLowerCase() === invoice.status?.toLowerCase()
        );
        if (statusObj) {
          statusId = String(statusObj.id);
        }
      }
    }

    setFormData({
      ...invoice,
      custo_envio: custoEnvioFormatted,
      caixas_malas: caixasMalasIds,
      // Usar o ID do status como valor
      status: statusId || '',
      // Garantir que codigo_rastreamento seja igual ao numero
      codigo_rastreamento:
        invoice.numero ||
        invoice.numero_documento ||
        invoice.codigo_rastreamento ||
        '',
    });
    setEditingId(invoice.id);
    setShowModal(true);
  };

  const handleDelete = id => {
    if (!isInvoicesAdmin) return;
    setInvoiceToDelete(id);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!invoiceToDelete) return;
    if (!isInvoicesAdmin) return;

    try {
      await apiService.deleteInvoice(invoiceToDelete);
      // Recarregar lista de invoices
      await loadInvoices();
      await loadAllInvoices(); // Recarregar todas as invoices para verificação
      setShowConfirmDialog(false);
      setInvoiceToDelete(null);
      showSuccess('Fatura excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir invoice:', error);
      showWarning(error?.message || 'Erro ao excluir fatura');
      setShowConfirmDialog(false);
      setInvoiceToDelete(null);
    }
  };

  const handleOpenModal = () => {
    if (!isInvoicesAdmin) return;
    const newInvoiceNumber = generateInvoiceNumber();

    // Buscar o status "Nova" na lista de status
    let novaStatusId = '';
    if (invoiceStatuses.length > 0) {
      const novaStatus = invoiceStatuses.find(
        s =>
          s.status_invoice?.toLowerCase().includes('nova') ||
          s.status_invoice?.toLowerCase().includes('novo')
      );
      if (novaStatus) {
        novaStatusId = String(novaStatus.id);
      }
    }

    // Se não encontrou "Nova", usar o primeiro status disponível ou 'pendente' como fallback
    if (!novaStatusId && invoiceStatuses.length > 0) {
      novaStatusId = String(invoiceStatuses[0].id);
    } else if (!novaStatusId) {
      novaStatusId = 'pendente';
    }

    setFormData({
      numero: newInvoiceNumber,
      codigo_rastreamento: newInvoiceNumber, // Usar o mesmo número como código de rastreamento
      cliente: '',
      cliente_email: '',
      cliente_telefone: '',
      cliente_cep: '',
      cliente_endereco: '',
      cliente_numero: '',
      cliente_cidade: '',
      cliente_estado: '',
      cliente_pais: '',
      origem: '',
      destino: '',
      caixas_malas: [],
      status: novaStatusId, // Status "Nova" setado automaticamente
      etapas: [],
      fornecedor_id: '',
      custo_envio: '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      numero: '',
      codigo_rastreamento: '',
      cliente: '',
      cliente_email: '',
      cliente_telefone: '',
      cliente_cep: '',
      cliente_numero: '',
      cliente_estado: '',
      cliente_cidade: '',
      cliente_pais: '',
      origem: '',
      destino: '',
      caixas_malas: [],
      status: 'pendente',
      etapas: [],
    });
    setEditingId(null);
    setCurrentBox('');
  };

  const handleExportExcel = invoice => {
    exportInvoiceToExcel(invoice, boxes);
  };

  const handleOpenInvoiceLabelModal = invoice => {
    setInvoiceForLabel(invoice);
    setInvoiceLabelMalasQty(1);
    setShowInvoiceLabelModal(true);
  };

  const handleCloseInvoiceLabelModal = () => {
    setShowInvoiceLabelModal(false);
    setInvoiceForLabel(null);
    setInvoiceLabelMalasQty(1);
  };

  const handleInvoiceLabelMalasQtyChange = e => {
    const raw = parseInt(e.target.value, 10);
    if (Number.isNaN(raw) || raw < 1) {
      setInvoiceLabelMalasQty(1);
      return;
    }
    if (raw > 999) {
      setInvoiceLabelMalasQty(999);
      return;
    }
    setInvoiceLabelMalasQty(raw);
  };

  const handleDownloadAllInvoiceLabelsPng = async () => {
    const root = invoiceLabelsPreviewRef.current;
    if (!root) {
      showWarning('Nenhuma etiqueta para exportar.');
      return;
    }
    const nodes = root.querySelectorAll('[data-invoice-label-sheet]');
    if (!nodes.length) {
      showWarning('Nenhuma etiqueta para exportar.');
      return;
    }
    const base = (invoiceForLabel?.numero || 'invoice')
      .toString()
      .replace(/[^\w-]/g, '_');
    try {
      setInvoiceLabelDownloading(true);
      const padW = nodes.length > 99 ? 3 : 2;
      for (let i = 0; i < nodes.length; i += 1) {
        const idx = String(i + 1).padStart(padW, '0');
        await exportInvoiceLabelPaperToPng(nodes[i], `${base}-${idx}`);
        await new Promise(r => setTimeout(r, 400));
      }
      showSuccess(
        `${nodes.length} arquivo(s) PNG (15×10 cm @ 300 DPI). Imprima em escala 100%.`
      );
    } catch (err) {
      showWarning(
        err?.message || 'Não foi possível gerar uma ou mais imagens.'
      );
    } finally {
      setInvoiceLabelDownloading(false);
    }
  };

  const handleOpenTrackingHistory = async invoice => {
    setSelectedInvoiceForTracking(invoice);
    setShowTrackingHistoryModal(true);
    setEditingHistoryId(null);

    // Carregar histórico primeiro
    const historyList = await loadTrackingHistory(invoice.id);

    // Pegar o último status registrado (mais recente por evento_em ou id)
    let lastStatus = '';
    if (historyList && historyList.length > 0) {
      // Ordenar por evento_em (mais recente primeiro) ou por id (maior id = mais recente)
      const sortedHistory = [...historyList].sort((a, b) => {
        if (a.evento_em && b.evento_em) {
          return new Date(b.evento_em) - new Date(a.evento_em);
        }
        return (b.id || 0) - (a.id || 0);
      });
      const lastItem = sortedHistory[0];
      if (lastItem && lastItem.status) {
        // Se o status é um ID numérico, manter como está. Se for string (nome), buscar o ID correspondente
        const statusId =
          typeof lastItem.status === 'number'
            ? lastItem.status
            : typeof lastItem.status === 'string' &&
                /^\d+$/.test(lastItem.status)
              ? parseInt(lastItem.status)
              : invoiceStatuses.find(
                  s =>
                    s.status_invoice === lastItem.status ||
                    s.status_invoice?.toLowerCase() ===
                      lastItem.status?.toLowerCase()
                )?.id || '';
        lastStatus = statusId ? String(statusId) : '';
      }
    }

    setTrackingForm({
      status: lastStatus,
      descricao: '',
      local_pacote: '',
      localizacao: '',
      latitude: '',
      longitude: '',
    });

    // Obter localização automaticamente quando abrir o modal
    await updateLocationFromDevice();
  };

  const loadTrackingHistory = async invoiceId => {
    try {
      setLoadingHistory(true);
      const response = await apiService.getTrackingHistory(invoiceId);

      // Normalizar a resposta da API usando normalizeApiData
      let historyList = normalizeApiData(response);

      // Garantir que seja um array
      if (!Array.isArray(historyList)) {
        // Se for um objeto único, converter para array
        if (historyList && typeof historyList === 'object' && historyList.id) {
          historyList = [historyList];
        } else {
          historyList = [];
        }
      }

      setTrackingHistory(historyList);
      return historyList;
    } catch (error) {
      console.error('Erro ao carregar histórico de rastreio:', error);
      showWarning('Erro ao carregar histórico de rastreio');
      setTrackingHistory([]);
      return [];
    } finally {
      setLoadingHistory(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalização não é suportada pelo navegador'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async position => {
          const { latitude, longitude } = position.coords;

          // Fazer reverse geocoding para obter endereço
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
              {
                headers: {
                  'User-Agent': 'SendingTo Admin',
                },
              }
            );
            const data = await response.json();

            // Formatar endereço: cidade, estado
            let locationText = '';
            if (data.address) {
              const city =
                data.address.city ||
                data.address.town ||
                data.address.village ||
                '';
              const state = data.address.state || '';
              locationText = city ? (state ? `${city}, ${state}` : city) : '';
            }

            resolve({
              latitude,
              longitude,
              localizacao: locationText || `${latitude}, ${longitude}`,
            });
          } catch (error) {
            // Se falhar o reverse geocoding, ainda retornar as coordenadas
            resolve({
              latitude,
              longitude,
              localizacao: `${latitude}, ${longitude}`,
            });
          }
        },
        error => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const updateLocationFromDevice = async () => {
    try {
      const location = await getCurrentLocation();
      setTrackingForm(prev => ({
        ...prev,
        latitude: String(location.latitude),
        longitude: String(location.longitude),
        localizacao: location.localizacao,
      }));
    } catch (error) {
      console.error('Erro ao obter localização:', error);
      showWarning(
        'Não foi possível obter a localização. Verifique as permissões do navegador.'
      );
    }
  };

  const handleSubmitTrackingHistory = async e => {
    e.preventDefault();
    if (!selectedInvoiceForTracking) return;

    try {
      // Enviar ID do status diretamente
      const statusId = trackingForm.status
        ? parseInt(trackingForm.status)
        : undefined;

      const historyData = {
        invoice_id: selectedInvoiceForTracking.id,
        status: statusId,
        descricao: trackingForm.descricao || undefined,
        local_pacote: trackingForm.local_pacote || undefined,
        localizacao: trackingForm.localizacao || undefined,
        latitude: trackingForm.latitude
          ? parseFloat(trackingForm.latitude)
          : undefined,
        longitude: trackingForm.longitude
          ? parseFloat(trackingForm.longitude)
          : undefined,
      };

      if (editingHistoryId) {
        // Atualizar - não alterar evento_em (data/hora original é preservada)
        await apiService.updateTrackingHistory(editingHistoryId, historyData);
        showSuccess('Status atualizado com sucesso!');
      } else {
        // Criar - capturar data/hora em tempo real no timezone de Brasília
        historyData.evento_em = getBrazilianDateTime();
        await apiService.createTrackingHistory(historyData);
        showSuccess('Status adicionado com sucesso!');
      }

      // Recarregar histórico e lista de invoices
      const historyList = await loadTrackingHistory(
        selectedInvoiceForTracking.id
      );
      await loadInvoices();

      // Pegar o último status registrado (mais recente por evento_em ou id) para pré-selecionar
      let lastStatus = '';
      if (historyList && historyList.length > 0) {
        // Ordenar por evento_em (mais recente primeiro) ou por id (maior id = mais recente)
        const sortedHistory = [...historyList].sort((a, b) => {
          if (a.evento_em && b.evento_em) {
            return new Date(b.evento_em) - new Date(a.evento_em);
          }
          return (b.id || 0) - (a.id || 0);
        });
        const lastItem = sortedHistory[0];
        if (lastItem && lastItem.status) {
          // Se o status é um ID numérico, manter como está. Se for string (nome), buscar o ID correspondente
          const statusId =
            typeof lastItem.status === 'number'
              ? lastItem.status
              : typeof lastItem.status === 'string' &&
                  /^\d+$/.test(lastItem.status)
                ? parseInt(lastItem.status)
                : invoiceStatuses.find(
                    s =>
                      s.status_invoice === lastItem.status ||
                      s.status_invoice?.toLowerCase() ===
                        lastItem.status?.toLowerCase()
                  )?.id || '';
          lastStatus = statusId ? String(statusId) : '';
        }
      }

      // Limpar formulário mas manter o último status selecionado
      setTrackingForm({
        status: lastStatus,
        descricao: '',
        local_pacote: '',
        localizacao: '',
        latitude: '',
        longitude: '',
      });
      setEditingHistoryId(null);
    } catch (error) {
      console.error('Erro ao salvar histórico:', error);
      showWarning(error?.message || 'Erro ao salvar histórico de rastreio');
    }
  };

  const handleEditHistory = historyItem => {
    setEditingHistoryId(historyItem.id);

    // Converter texto do status para ID se existir no invoiceStatuses
    let statusId = '';
    if (historyItem.status && invoiceStatuses.length > 0) {
      const statusObj = invoiceStatuses.find(
        s =>
          s.status_invoice === historyItem.status ||
          s.status_invoice?.toLowerCase() === historyItem.status?.toLowerCase()
      );
      if (statusObj) {
        statusId = statusObj.id;
      } else {
        // Se não encontrar, usar o texto como está (fallback)
        statusId = historyItem.status;
      }
    } else if (historyItem.status) {
      statusId = historyItem.status;
    }

    setTrackingForm({
      status: statusId,
      descricao: historyItem.descricao || '',
      local_pacote: historyItem.local_pacote || '',
      localizacao: historyItem.localizacao || '',
      latitude: historyItem.latitude ? String(historyItem.latitude) : '',
      longitude: historyItem.longitude ? String(historyItem.longitude) : '',
    });
  };

  const handleDeleteHistory = async historyId => {
    if (
      !window.confirm('Tem certeza que deseja excluir este item do histórico?')
    ) {
      return;
    }

    try {
      await apiService.deleteTrackingHistory(historyId);
      showSuccess('Item excluído com sucesso!');
      if (selectedInvoiceForTracking) {
        await loadTrackingHistory(selectedInvoiceForTracking.id);
      }
    } catch (error) {
      console.error('Erro ao excluir histórico:', error);
      showWarning(error?.message || 'Erro ao excluir item do histórico');
    }
  };

  // Função para obter o nome do status pelo ID
  const getStatusNameById = statusId => {
    if (!statusId) return '';
    // Se já for string (nome), retornar como está
    if (typeof statusId === 'string' && !/^\d+$/.test(statusId)) {
      return statusId;
    }
    // Buscar na lista de invoiceStatuses
    const statusIdNum =
      typeof statusId === 'string' ? parseInt(statusId) : statusId;
    const statusObj = invoiceStatuses.find(s => s.id === statusIdNum);
    return statusObj ? statusObj.status_invoice : statusId;
  };

  // Status na tabela: apenas ícone; nome do status no tooltip ao passar o mouse
  const getStatusIconEl = status => {
    const statusName = getStatusNameById(status) || 'Pendente';
    const statusStr = (
      typeof statusName === 'string' ? statusName : String(statusName)
    )
      .toLowerCase()
      .trim();
    const statusMap = {
      nova: { Icon: HiInformationCircle, color: '#17A2B8' },
      novo: { Icon: HiInformationCircle, color: '#17A2B8' },
      new: { Icon: HiInformationCircle, color: '#17A2B8' },
      'em progresso': { Icon: HiClock, color: '#f59e0b' },
      'in-progress': { Icon: HiClock, color: '#f59e0b' },
      pendente: { Icon: HiClock, color: '#f59e0b' },
      pending: { Icon: HiClock, color: '#f59e0b' },
      paga: { Icon: HiCheckCircle, color: '#10b981' },
      concluída: { Icon: HiCheckCircle, color: '#10b981' },
      concluido: { Icon: HiCheckCircle, color: '#10b981' },
      complete: { Icon: HiCheckCircle, color: '#10b981' },
      cancelada: { Icon: HiXCircle, color: '#ef4444' },
      cancelado: { Icon: HiXCircle, color: '#ef4444' },
      cancelled: { Icon: HiXCircle, color: '#ef4444' },
    };
    const { Icon, color } = statusMap[statusStr] || {
      Icon: HiClock,
      color: '#6b7280',
    };
    return (
      <span
        className='invoice-status-icon'
        title={statusName}
        style={{ color, display: 'inline-flex', alignItems: 'center' }}
      >
        <Icon size={20} />
      </span>
    );
  };

  const filteredInvoices = invoices.filter(
    invoice =>
      invoice.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.cliente.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  return (
    <div className='page-container'>
      <div className='breadcrumb'>
        <span>Painel</span>
        <span>Invoices</span>
      </div>

      <div className='page-header'>
        <div>
          <h1>Invoices</h1>
          <p>Gerencie suas faturas</p>
        </div>
        {isInvoicesAdmin && (
          <button className='btn btn-primary' onClick={handleOpenModal}>
            <HiPlus size={20} />
            Nova Fatura
          </button>
        )}
      </div>

      <div className='page-actions'>
        <div className='search-box'>
          <HiSearch className='search-icon' size={20} />
          <input
            type='text'
            placeholder='Buscar faturas...'
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className='card'>
        {loading ? (
          <SkeletonTable rows={8} columns={isInvoicesAdmin ? 9 : 8} />
        ) : paginatedInvoices.length === 0 ? (
          <div className='empty-state'>
            <p>
              {searchTerm
                ? 'Nenhuma fatura encontrada'
                : 'Nenhuma fatura cadastrada'}
            </p>
          </div>
        ) : (
          <>
            <div className='table-container'>
              <table className='data-table'>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código Invoice</th>
                    <th>Destinatário</th>
                    <th>Data</th>
                    <th>Caixas/Malas</th>
                    {isInvoicesAdmin && <th>Valor Total</th>}
                    <th>Peso Total</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((invoice, index) => {
                    // Calcular valor total: usar valor_total da API ou calcular a partir dos pacotes
                    let invoiceTotal = 0;
                    if (invoice.valor) {
                      const valorStr =
                        typeof invoice.valor === 'string'
                          ? invoice.valor
                          : String(invoice.valor);
                      invoiceTotal =
                        parseFloat(
                          valorStr
                            .replace('R$', '')
                            .replace(/\./g, '')
                            .replace(',', '.')
                            .trim()
                        ) || 0;
                    } else if (
                      Array.isArray(invoice.caixas_malas) &&
                      invoice.caixas_malas.length > 0
                    ) {
                      // Se caixas_malas é array de objetos (pacotes), calcular a partir deles
                      invoiceTotal = invoice.caixas_malas.reduce(
                        (sum, pacote) => {
                          if (pacote && pacote.valor_declarado) {
                            const valorStr =
                              typeof pacote.valor_declarado === 'string'
                                ? pacote.valor_declarado
                                : String(pacote.valor_declarado);
                            const value =
                              parseFloat(
                                valorStr
                                  .replace('R$', '')
                                  .replace(/\./g, '')
                                  .replace(',', '.')
                                  .trim()
                              ) || 0;
                            return sum + value;
                          }
                          return sum;
                        },
                        0
                      );
                    }
                    const formattedTotal =
                      invoiceTotal > 0
                        ? invoiceTotal.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        : displayCurrency(
                            invoice.valor_total || invoice.valor || 0
                          );

                    return (
                      <tr key={invoice.id}>
                        <td>{startIndex + index + 1}</td>
                        <td>
                          <strong>{invoice.numero}</strong>
                        </td>
                        <td>{invoice.cliente}</td>
                        <td>
                          {new Date(invoice.data).toLocaleDateString('pt-BR')}
                        </td>
                        <td>{invoice.caixas_malas?.length || 0} item(s)</td>
                        {isInvoicesAdmin && (
                          <td>
                            <strong>{formattedTotal}</strong>
                          </td>
                        )}
                        <td>
                          <strong>
                            {invoice.peso_total
                              ? `${parseFloat(invoice.peso_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
                              : '0,00 kg'}
                          </strong>
                        </td>
                        <td>{getStatusIconEl(invoice.status)}</td>
                        <td>
                          <div className='actions'>
                            {isInvoicesAdmin && (
                              <button
                                type='button'
                                className='btn-icon btn-edit'
                                onClick={() => handleEdit(invoice)}
                                title='Editar'
                              >
                                <HiPencil size={18} />
                              </button>
                            )}
                            <button
                              type='button'
                              className='btn-icon'
                              onClick={() =>
                                navigate(`/tracking?code=${invoice.numero}`)
                              }
                              title='Rastrear'
                            >
                              <HiLocationMarker size={18} />
                            </button>
                            <div
                              className='actions-menu-wrapper'
                              style={{ position: 'relative' }}
                            >
                              <button
                                type='button'
                                className='btn-icon btn-menu-toggle'
                                onClick={e => {
                                  e.stopPropagation();
                                  if (openMenuId === invoice.id) {
                                    setOpenMenuId(null);
                                  } else {
                                    const rect =
                                      e.currentTarget.getBoundingClientRect();
                                    setMenuPosition({
                                      top: rect.top - 10,
                                      right: window.innerWidth - rect.right,
                                    });
                                    setOpenMenuId(invoice.id);
                                  }
                                }}
                                title='Mais opções'
                              >
                                <HiDotsVertical size={18} />
                              </button>
                              {openMenuId === invoice.id && (
                                <>
                                  <div
                                    className='actions-dropdown-overlay'
                                    onClick={() => setOpenMenuId(null)}
                                  />
                                  <div
                                    className='actions-dropdown-menu'
                                    style={{
                                      top: `${menuPosition.top}px`,
                                      right: `${menuPosition.right}px`,
                                    }}
                                  >
                                    <button
                                      className='dropdown-menu-item'
                                      onClick={async () => {
                                        setOpenMenuId(null);
                                        await handleOpenTrackingHistory(
                                          invoice
                                        );
                                      }}
                                    >
                                      <HiPlus size={16} />
                                      <span>Status</span>
                                    </button>
                                    <button
                                      className='dropdown-menu-item'
                                      onClick={() => {
                                        handleExportExcel(invoice);
                                        setOpenMenuId(null);
                                      }}
                                    >
                                      <HiDownload size={16} />
                                      <span>Exportar Excel</span>
                                    </button>
                                    <button
                                      className='dropdown-menu-item'
                                      onClick={() => {
                                        handleOpenInvoiceLabelModal(invoice);
                                        setOpenMenuId(null);
                                      }}
                                    >
                                      <HiTag size={16} />
                                      <span>Etiqueta da Invoice</span>
                                    </button>
                                    <button
                                      className='dropdown-menu-item'
                                      onClick={() => {
                                        // Garantir que codigos_acesso está no formato correto
                                        let codigos = invoice.codigos_acesso;
                                        // Se for string JSON, fazer parse
                                        if (
                                          typeof codigos === 'string' &&
                                          codigos.trim() !== ''
                                        ) {
                                          try {
                                            codigos = JSON.parse(codigos);
                                          } catch (e) {
                                            console.error(
                                              'Erro ao fazer parse de codigos_acesso:',
                                              e
                                            );
                                            codigos = null;
                                          }
                                        }
                                        // Garantir formato padrão com todas as chaves
                                        const codigosFormatados =
                                          codigos && typeof codigos === 'object'
                                            ? {
                                                administrador:
                                                  codigos.administrador || '',
                                                gerente: codigos.gerente || '',
                                                transportador:
                                                  codigos.transportador || '',
                                                entregador:
                                                  codigos.entregador || '',
                                              }
                                            : {
                                                administrador: '',
                                                gerente: '',
                                                transportador: '',
                                                entregador: '',
                                              };

                                        setSelectedInvoiceCodes({
                                          ...invoice,
                                          codigos_acesso: codigosFormatados,
                                        });
                                        setShowCodesModal(true);
                                        setOpenMenuId(null);
                                      }}
                                    >
                                      <HiKey size={16} />
                                      <span>Códigos de Acesso</span>
                                    </button>
                                    {isInvoicesAdmin && (
                                      <>
                                        <div className='dropdown-divider'></div>
                                        <button
                                          className='dropdown-menu-item danger'
                                          onClick={() => {
                                            handleDelete(invoice.id);
                                            setOpenMenuId(null);
                                          }}
                                        >
                                          <HiTrash size={16} />
                                          <span>Excluir</span>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='table-footer'>
              <div className='pagination-info'>
                {filteredInvoices.length > 0 ? (
                  <span>
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredInvoices.length
                    )}{' '}
                    de {filteredInvoices.length} registro
                    {filteredInvoices.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className='items-per-page'>
                <span>Mostrar:</span>
                <Select
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className='select-sm'
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                  ]}
                />
              </div>
              <div className='pagination'>
                <button
                  className='pagination-btn'
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className='pagination-info'>
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className='pagination-btn'
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de Invoice */}
      {showModal && (
        <div className='modal-overlay'>
          <div
            className='modal-content modal-large'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h2>{editingId ? 'Editar Fatura' : 'Nova Fatura'}</h2>
              <button className='modal-close' onClick={handleCloseModal}>
                <HiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className='modal-form'>
              <div className='form-row'>
                <div className='form-group'>
                  <label>Código de Rastreamento</label>
                  <input
                    type='text'
                    value={formData.numero}
                    onChange={e =>
                      setFormData({ ...formData, numero: e.target.value })
                    }
                    required
                    placeholder='INV001'
                    readOnly
                  />
                </div>
                <div className='form-group'>
                  <label>Cliente Destinatário</label>
                  <ClientSearchSelect
                    id='field-invoice-cliente-destinatario'
                    value={formData.cliente}
                    valueMode='nome'
                    linkEntityLabel='fatura'
                    clients={clients}
                    onRefreshClients={loadClients}
                    onNotify={({ type, message }) => {
                      if (type === 'success') showSuccess(message);
                      else showWarning(message);
                    }}
                    onChange={payload => {
                      const {
                        cliente_id,
                        cliente_nome,
                        cliente_email: emailFromPayload,
                        cliente_telefone: telFromPayload,
                      } = payload;
                      const c = clients.find(
                        x => String(x.id) === String(cliente_id)
                      );
                      setFormData({
                        ...formData,
                        cliente: cliente_nome,
                        cliente_email:
                          emailFromPayload !== undefined
                            ? emailFromPayload
                            : c?.email || '',
                        cliente_telefone:
                          telFromPayload !== undefined
                            ? telFromPayload
                            : c?.telefone || '',
                      });
                    }}
                    placeholder='Selecione ou busque um cliente'
                    disabled={saving}
                  />
                </div>
                <div className='form-group'>
                  <label>Email do Cliente</label>
                  <input
                    type='email'
                    value={formData.cliente_email}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        cliente_email: e.target.value,
                      })
                    }
                    placeholder='email@exemplo.com'
                  />
                </div>
                <div className='form-group'>
                  <label>Telefone do Cliente</label>
                  <PhoneInput
                    value={formData.cliente_telefone}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        cliente_telefone: e.target.value,
                      })
                    }
                    placeholder='Número do telefone'
                  />
                </div>
              </div>

              <div className='form-section-title'>
                <HiLocationMarker size={18} />
                <span>Endereço do Cliente</span>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>CEP/Zip Code</label>
                  <input
                    type='text'
                    value={formData.cliente_cep}
                    onChange={e =>
                      setFormData({ ...formData, cliente_cep: e.target.value })
                    }
                    placeholder='00000-000'
                    maxLength={9}
                  />
                </div>
                <div className='form-group'>
                  <label>Endereço</label>
                  <input
                    type='text'
                    value={formData.cliente_endereco}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        cliente_endereco: e.target.value,
                      })
                    }
                    placeholder='Rua, Avenida, etc.'
                  />
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>Número</label>
                  <input
                    type='text'
                    value={formData.cliente_numero}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        cliente_numero: e.target.value,
                      })
                    }
                    placeholder='123'
                  />
                </div>
                <div className='form-group'>
                  <label>Cidade</label>
                  <input
                    type='text'
                    value={formData.cliente_cidade}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        cliente_cidade: e.target.value,
                      })
                    }
                    placeholder='Nome da cidade'
                  />
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>
                    Estado <span className='optional-label'>(Opcional)</span>
                  </label>
                  <input
                    type='text'
                    value={formData.cliente_estado}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        cliente_estado: e.target.value,
                      })
                    }
                    placeholder='Estado ou Província'
                  />
                </div>
                <div className='form-group'>
                  <label>País</label>
                  <input
                    type='text'
                    value={formData.cliente_pais}
                    onChange={e =>
                      setFormData({ ...formData, cliente_pais: e.target.value })
                    }
                    placeholder='Nome do país'
                  />
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>Origem</label>
                  <input
                    type='text'
                    value={formData.origem || ''}
                    onChange={e =>
                      setFormData({ ...formData, origem: e.target.value })
                    }
                    required
                    placeholder='Cidade, País'
                  />
                </div>
                <div className='form-group'>
                  <label>Destino</label>
                  <input
                    type='text'
                    value={formData.destino || ''}
                    onChange={e =>
                      setFormData({ ...formData, destino: e.target.value })
                    }
                    required
                    placeholder='Cidade, País'
                  />
                </div>
                <div className='form-group'>
                  <label>Status</label>
                  <Select
                    value={formData.status}
                    onChange={e =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    required
                    options={
                      invoiceStatuses.length > 0
                        ? invoiceStatuses.map(status => ({
                            value: status.id,
                            label: status.status_invoice,
                          }))
                        : [
                            { value: 'pendente', label: 'Pendente' },
                            { value: 'paga', label: 'Paga' },
                            { value: 'cancelada', label: 'Cancelada' },
                          ]
                    }
                  />
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>Fornecedor</label>
                  <Select
                    value={formData.fornecedor_id}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        fornecedor_id: e.target.value,
                      })
                    }
                    options={[
                      { value: '', label: 'Selecione um fornecedor' },
                      ...fornecedores.map(f => ({
                        value: f.id,
                        label:
                          f.razao_social ||
                          f.nome_fantasia ||
                          f.nome ||
                          `Fornecedor ${f.id}`,
                      })),
                    ]}
                  />
                </div>
                <div className='form-group'>
                  <label>Custo de Envio</label>
                  <div style={{ position: 'relative' }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        pointerEvents: 'none',
                        fontWeight: '500',
                      }}
                    >
                      R$
                    </span>
                    <input
                      type='text'
                      value={formData.custo_envio}
                      onChange={e => {
                        const inputValue = e.target.value;
                        // Permite apenas números
                        const numbers = inputValue.replace(/\D/g, '');
                        // Formata como moeda brasileira (0,00)
                        const formatted = handleCurrencyInput(numbers);
                        setFormData({ ...formData, custo_envio: formatted });
                      }}
                      onBlur={e => {
                        // Garante formatação correta ao sair do campo
                        const numbers = e.target.value.replace(/\D/g, '');
                        const formatted = handleCurrencyInput(numbers);
                        setFormData({ ...formData, custo_envio: formatted });
                      }}
                      placeholder='0,00'
                      style={{ paddingLeft: '40px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Seção de Caixas/Malas */}
              <div className='packages-section'>
                <div className='section-header'>
                  <h3>Caixas / Malas</h3>
                  <span>
                    Valor Total: <strong>{calculateInvoiceTotal()}</strong>
                  </span>
                </div>
                <div className='add-package-form'>
                  <Select
                    value={currentBox}
                    onChange={e => setCurrentBox(e.target.value)}
                    placeholder='Selecione uma caixa/mala'
                    options={[
                      ...boxes
                        .filter(b => {
                          // Não mostrar packages já selecionados nesta fatura
                          if (formData.caixas_malas.includes(b.id)) {
                            return false;
                          }

                          // Verificar se a caixa está em alguma invoice
                          // Se estiver editando, excluir a invoice atual da verificação
                          // Garantir que allInvoices é um array válido
                          const validAllInvoices = Array.isArray(allInvoices)
                            ? allInvoices
                            : [];
                          const invoicesToCheck = editingId
                            ? validAllInvoices.filter(
                                inv =>
                                  inv && Number(inv.id) !== Number(editingId)
                              )
                            : validAllInvoices;

                          // Verificar se esta caixa está em alguma invoice
                          const isInAnyInvoice =
                            invoicesToCheck.length > 0 &&
                            invoicesToCheck.some(inv => {
                              if (!inv) return false;
                              // Verificar se a caixa está no array de pacotes ou caixas_malas
                              const invoiceBoxes =
                                inv.pacotes ||
                                inv.caixas_malas ||
                                inv.boxes ||
                                [];

                              // Se é array de objetos, verificar pelo id
                              if (
                                Array.isArray(invoiceBoxes) &&
                                invoiceBoxes.length > 0
                              ) {
                                // Verificar se é array de objetos com propriedade id
                                if (
                                  typeof invoiceBoxes[0] === 'object' &&
                                  invoiceBoxes[0] !== null
                                ) {
                                  // Pode ter id diretamente ou dentro de um objeto
                                  return invoiceBoxes.some(pkg => {
                                    const pkgId =
                                      pkg.id ||
                                      pkg.package_id ||
                                      pkg.box_id ||
                                      pkg.caixa_id;
                                    return (
                                      pkgId && Number(pkgId) === Number(b.id)
                                    );
                                  });
                                }
                                // Se é array de IDs (números ou strings), comparar como números
                                return invoiceBoxes.some(boxId => {
                                  const idToCompare =
                                    typeof boxId === 'object' && boxId !== null
                                      ? boxId.id ||
                                        boxId.package_id ||
                                        boxId.box_id ||
                                        boxId.caixa_id
                                      : boxId;
                                  return (
                                    idToCompare !== null &&
                                    idToCompare !== undefined &&
                                    Number(idToCompare) === Number(b.id)
                                  );
                                });
                              }

                              return false;
                            });

                          // Se está em alguma invoice, não mostrar (mostrar apenas caixas não vinculadas)
                          if (isInAnyInvoice) {
                            return false;
                          }

                          // Validar se origem e destino da invoice correspondem à caixa
                          // Só validar se ambos (origem e destino) estiverem preenchidos na invoice
                          if (formData.origem && formData.destino) {
                            // Se a caixa não tem origem ou destino, não filtrar (pode ser legado)
                            if (!b.origem || !b.destino) {
                              // Permitir caixas sem origem/destino se a invoice tiver
                              // (pode ser uma caixa antiga sem esses campos)
                            } else {
                              const origemMatch =
                                b.origem.toLowerCase().trim() ===
                                formData.origem.toLowerCase().trim();
                              const destinoMatch =
                                b.destino.toLowerCase().trim() ===
                                formData.destino.toLowerCase().trim();

                              // Se origem ou destino não correspondem, não mostrar
                              if (!origemMatch || !destinoMatch) {
                                return false;
                              }
                            }
                          }

                          return true;
                        })
                        .map(box => ({
                          value: box.id,
                          label: `${box.codigo} - ${box.cliente_nome} (${displayCurrency(box.valor_remessa)})`,
                        })),
                    ]}
                  />
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={handleAddBox}
                  >
                    <HiPlus size={18} />
                    Adicionar
                  </button>
                </div>
                {formData.caixas_malas.length > 0 && (
                  <div className='packages-list'>
                    {formData.caixas_malas.map(boxId => {
                      const box = boxes.find(b => b.id === boxId);
                      return box ? (
                        <div key={boxId} className='package-item'>
                          <span>
                            {box.codigo} - {box.cliente_nome}
                          </span>
                          <span>
                            <strong>
                              {displayCurrency(box.valor_remessa)}
                            </strong>
                          </span>
                          <button
                            type='button'
                            className='btn-remove'
                            onClick={() => handleRemoveBox(boxId)}
                          >
                            <HiX size={18} />
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              <div className='modal-actions'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <HiRefresh
                        size={18}
                        style={{
                          animation: 'spin 1s linear infinite',
                          display: 'inline-block',
                          marginRight: '8px',
                        }}
                      />
                      {editingId ? 'Atualizando...' : 'Salvando...'}
                    </>
                  ) : (
                    <>{editingId ? 'Atualizar' : 'Salvar'} Fatura</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title='Confirmar Exclusão'
        message='Tem certeza que deseja excluir esta fatura?'
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setInvoiceToDelete(null);
        }}
        confirmText='Excluir'
        cancelText='Cancelar'
        type='danger'
      />

      <div className='notifications-container'>
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>

      {/* Modal de Histórico de Rastreio */}
      {showTrackingHistoryModal && selectedInvoiceForTracking && (
        <div
          className='modal-overlay'
          onClick={() => {
            setShowTrackingHistoryModal(false);
            setSelectedInvoiceForTracking(null);
            setTrackingHistory([]);
            setEditingHistoryId(null);
            setTrackingForm({
              status: '',
              descricao: '',
              local_pacote: '',
              localizacao: '',
              latitude: '',
              longitude: '',
              evento_em: getBrazilianDateTime(),
            });
          }}
        >
          <div
            className='modal-content modal-large'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h2>
                Histórico de Rastreio - Invoice{' '}
                {selectedInvoiceForTracking.numero}
              </h2>
              <button
                className='modal-close'
                onClick={() => {
                  setShowTrackingHistoryModal(false);
                  setSelectedInvoiceForTracking(null);
                  setTrackingHistory([]);
                  setEditingHistoryId(null);
                  setTrackingForm({
                    status: '',
                    descricao: '',
                    local_pacote: '',
                    localizacao: '',
                    latitude: '',
                    longitude: '',
                  });
                }}
              >
                <HiX size={24} />
              </button>
            </div>

            <div
              className='modal-form'
              style={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
              {/* Formulário */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3
                  style={{
                    marginBottom: '1rem',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                  }}
                >
                  {editingHistoryId ? 'Editar Status' : 'Adicionar Status'}
                </h3>
                <form onSubmit={handleSubmitTrackingHistory}>
                  <div className='form-row'>
                    <div className='form-group'>
                      <label>Status *</label>
                      <Select
                        value={trackingForm.status}
                        onChange={e =>
                          setTrackingForm({
                            ...trackingForm,
                            status: e.target.value,
                          })
                        }
                        placeholder='Selecione um status'
                        required
                        options={
                          invoiceStatuses.length > 0
                            ? invoiceStatuses.map(status => ({
                                value: status.id,
                                label: status.status_invoice,
                              }))
                            : []
                        }
                      />
                    </div>
                    <div className='form-group'>
                      <label>Local do Pacote</label>
                      <input
                        type='text'
                        value={trackingForm.local_pacote}
                        onChange={e =>
                          setTrackingForm({
                            ...trackingForm,
                            local_pacote: e.target.value,
                          })
                        }
                        placeholder='Ex: Depósito, Centro de Distribuição, etc.'
                      />
                    </div>
                  </div>
                  <div className='form-group'>
                    <label>Comentário/Descrição</label>
                    <textarea
                      value={trackingForm.descricao}
                      onChange={e =>
                        setTrackingForm({
                          ...trackingForm,
                          descricao: e.target.value,
                        })
                      }
                      placeholder='Descrição do evento ou comentário'
                      rows={3}
                    />
                  </div>
                  <div className='modal-actions'>
                    {editingHistoryId && (
                      <button
                        type='button'
                        className='btn btn-secondary'
                        onClick={() => {
                          setEditingHistoryId(null);
                          setTrackingForm({
                            status: '',
                            descricao: '',
                            local_pacote: '',
                            localizacao: '',
                            latitude: '',
                            longitude: '',
                            evento_em: getBrazilianDateTime(),
                          });
                        }}
                      >
                        Cancelar Edição
                      </button>
                    )}
                    <button type='submit' className='btn btn-primary'>
                      {editingHistoryId ? 'Atualizar' : 'Adicionar'} Status
                    </button>
                  </div>
                </form>
              </div>

              {/* Lista de Histórico */}
              <div
                style={{ borderTop: '2px solid #e5e7eb', paddingTop: '1.5rem' }}
              >
                <h3
                  style={{
                    marginBottom: '1rem',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                  }}
                >
                  Histórico
                </h3>
                {loadingHistory ? (
                  <div
                    style={{
                      padding: '1rem',
                      textAlign: 'center',
                      color: '#6b7280',
                    }}
                  >
                    Carregando histórico...
                  </div>
                ) : trackingHistory.length === 0 ? (
                  <div
                    style={{
                      padding: '1rem',
                      textAlign: 'center',
                      color: '#6b7280',
                    }}
                  >
                    Nenhum histórico encontrado
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    {trackingHistory.map(item => (
                      <div
                        key={item.id}
                        style={{
                          padding: '0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          backgroundColor: '#f9fafb',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            {item.status && (
                              <div style={{ marginBottom: '0.25rem' }}>
                                <strong>Status:</strong>{' '}
                                {getStatusNameById(item.status)}
                              </div>
                            )}
                            {item.descricao && (
                              <div
                                style={{
                                  marginBottom: '0.25rem',
                                  fontSize: '0.875rem',
                                  color: '#4b5563',
                                }}
                              >
                                {item.descricao}
                              </div>
                            )}
                            {item.local_pacote && (
                              <div
                                style={{
                                  marginBottom: '0.25rem',
                                  fontSize: '0.875rem',
                                  color: '#6b7280',
                                }}
                              >
                                <strong>Local do Pacote:</strong>{' '}
                                {item.local_pacote}
                              </div>
                            )}
                            {item.localizacao && (
                              <div
                                style={{
                                  marginBottom: '0.25rem',
                                  fontSize: '0.875rem',
                                  color: '#6b7280',
                                }}
                              >
                                <strong>Localização:</strong> {item.localizacao}
                              </div>
                            )}
                            {item.evento_em && (
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#9ca3af',
                                }}
                              >
                                {new Date(item.evento_em).toLocaleString(
                                  'pt-BR'
                                )}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              type='button'
                              className='btn-icon'
                              onClick={() => handleEditHistory(item)}
                              title='Editar'
                              style={{ padding: '0.25rem' }}
                            >
                              <HiPencil size={16} />
                            </button>
                            <button
                              type='button'
                              className='btn-icon btn-delete'
                              onClick={() => handleDeleteHistory(item.id)}
                              title='Excluir'
                              style={{ padding: '0.25rem' }}
                            >
                              <HiTrash size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Etiqueta da Invoice (15×10 cm, numeração por quantidade de malas) */}
      {showInvoiceLabelModal && invoiceForLabel && (
        <div className='modal-overlay' onClick={handleCloseInvoiceLabelModal}>
          <div
            className='modal-content invoice-invoice-label-modal'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h2>Etiqueta da Invoice — {invoiceForLabel.numero}</h2>
              <button
                type='button'
                className='modal-close'
                onClick={handleCloseInvoiceLabelModal}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className='modal-body invoice-invoice-label-modal__body'>
              <p className='invoice-invoice-label-modal__hint'>
                Etiqueta no Formato 15 cm × 10 cm.
              </p>
              <div className='form-group invoice-invoice-label-modal__qty-group'>
                <div className='invoice-invoice-label-modal__qty-with-btn'>
                  <div className='invoice-invoice-label-modal__qty-fields'>
                    <label
                      htmlFor='invoice-malas-qty'
                      className='invoice-invoice-label-modal__qty-label'
                    >
                      Quantidade de malas
                    </label>
                    <input
                      id='invoice-malas-qty'
                      type='number'
                      min={1}
                      max={999}
                      value={invoiceLabelMalasQty}
                      onChange={handleInvoiceLabelMalasQtyChange}
                      className='invoice-invoice-label-modal__qty-input'
                    />
                  </div>
                  <div className='invoice-invoice-label-modal__print-stack'>
                    <span
                      className='invoice-invoice-label-modal__print-heading'
                      id='invoice-label-print-heading'
                    >
                      Imprimir
                    </span>
                    <button
                      type='button'
                      className='btn btn-primary invoice-invoice-label-modal__download-btn'
                      aria-labelledby='invoice-label-print-heading'
                      onClick={handleDownloadAllInvoiceLabelsPng}
                      disabled={
                        invoiceLabelDownloading || invoiceLabelMalasQty < 1
                      }
                    >
                      <HiDownload size={16} />
                      {invoiceLabelDownloading
                        ? 'Gerando…'
                        : `Baixar ${invoiceLabelMalasQty} PNG`}
                    </button>
                  </div>
                </div>
                <span className='invoice-invoice-label-modal__qty-help'>
                  Gera {invoiceLabelMalasQty} etiqueta
                  {invoiceLabelMalasQty !== 1 ? 's' : ''} numerada
                  {invoiceLabelMalasQty !== 1 ? 's' : ''}.
                </span>
              </div>
              <div
                ref={invoiceLabelsPreviewRef}
                className='invoice-invoice-label-modal__preview'
              >
                {Array.from({ length: invoiceLabelMalasQty }, (_, i) => (
                  <InvoiceLabelSheet
                    key={i}
                    invoice={invoiceForLabel}
                    malaIndex={i + 1}
                    malaTotal={invoiceLabelMalasQty}
                  />
                ))}
              </div>
            </div>
            <div className='modal-footer invoice-invoice-label-modal__footer'>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={handleCloseInvoiceLabelModal}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Códigos de Acesso */}
      {showCodesModal && selectedInvoiceCodes && (
        <div
          className='modal-overlay'
          onClick={() => {
            setShowCodesModal(false);
            setSelectedInvoiceCodes(null);
          }}
        >
          <div
            className='modal-content'
            style={{ maxWidth: '600px', width: '90%' }}
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h2>Códigos de Acesso - Invoice {selectedInvoiceCodes.numero}</h2>
              <button
                className='modal-close'
                onClick={() => {
                  setShowCodesModal(false);
                  setSelectedInvoiceCodes(null);
                }}
              >
                <HiX size={24} />
              </button>
            </div>

            <div className='modal-body' style={{ padding: '1.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                {/* Administrador */}
                <div
                  style={{
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Administrador
                      </div>
                      <div
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                        }}
                      >
                        {selectedInvoiceCodes.codigos_acesso?.administrador ||
                          'Não gerado'}
                      </div>
                    </div>
                    {selectedInvoiceCodes.codigos_acesso?.administrador && (
                      <button
                        className='btn-icon'
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedInvoiceCodes.codigos_acesso.administrador
                          );
                          showSuccess('Código copiado!');
                        }}
                        title='Copiar código'
                      >
                        <HiClipboard size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Gerente */}
                <div
                  style={{
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Gerente
                      </div>
                      <div
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                        }}
                      >
                        {selectedInvoiceCodes.codigos_acesso?.gerente ||
                          'Não gerado'}
                      </div>
                    </div>
                    {selectedInvoiceCodes.codigos_acesso?.gerente && (
                      <button
                        className='btn-icon'
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedInvoiceCodes.codigos_acesso.gerente
                          );
                          showSuccess('Código copiado!');
                        }}
                        title='Copiar código'
                      >
                        <HiClipboard size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Transportador */}
                <div
                  style={{
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Transportador
                      </div>
                      <div
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                        }}
                      >
                        {selectedInvoiceCodes.codigos_acesso?.transportador ||
                          'Não gerado'}
                      </div>
                    </div>
                    {selectedInvoiceCodes.codigos_acesso?.transportador && (
                      <button
                        className='btn-icon'
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedInvoiceCodes.codigos_acesso.transportador
                          );
                          showSuccess('Código copiado!');
                        }}
                        title='Copiar código'
                      >
                        <HiClipboard size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Entregador */}
                <div
                  style={{
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Entregador
                      </div>
                      <div
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                        }}
                      >
                        {selectedInvoiceCodes.codigos_acesso?.entregador ||
                          'Não gerado'}
                      </div>
                    </div>
                    {selectedInvoiceCodes.codigos_acesso?.entregador && (
                      <button
                        className='btn-icon'
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedInvoiceCodes.codigos_acesso.entregador
                          );
                          showSuccess('Código copiado!');
                        }}
                        title='Copiar código'
                      >
                        <HiClipboard size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              className='modal-footer'
              style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}
            >
              <button
                type='button'
                className='btn btn-secondary'
                onClick={() => {
                  setShowCodesModal(false);
                  setSelectedInvoiceCodes(null);
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
